import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import API from '../../services/api';

const SupplierEditDialog = ({ open, onClose, supplier, onSuccess }) => {
  const [formData, setFormData] = useState({
    supplier_name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    business_id: '',
  });
  const [formStep, setFormStep] = useState('form');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  useEffect(() => {
    if (supplier && open) {
      setFormData({
        supplier_name: supplier.SupplierName || '',
        contact_person: supplier.ContactPerson || '',
        phone: supplier.Phone || supplier.SupplierPhone || '',
        email: supplier.Email || supplier.SupplierEmail || '',
        address: supplier.Address || supplier.SupplierAddress || '',
        business_id: supplier.SupplierBusinessID || '',
      });
    }
  }, [supplier, open]);

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      business_id: '',
    });
    setFormStep('form');
    setError('');
    isSubmittingRef.current = false; 
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleValidation = () => {
    setError('');
    if (!formData.supplier_name.trim()) {
      setError('שם הספק הוא שדה חובה');
      return;
    }
    setFormStep('confirm');
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        supplier_id: supplier.SupplierID,
        SupplierName: formData.supplier_name,
        ContactPerson: formData.contact_person,
        SupplierPhone: formData.phone,
        SupplierEmail: formData.email,
        SupplierAddress: formData.address,
        SupplierBusinessID: formData.business_id,
      };

      await API.put('/supplier/update/', payload);
      if (onSuccess) onSuccess();
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error updating supplier:', err);
      setError(err?.response?.data?.error || 'שגיאה בעדכון הספק');
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          fontSize: '1.4rem',
          '*': { fontSize: 'inherit !important' },
        },
      }}
    >
      <DialogTitle sx={{ fontSize: '2rem !important', fontWeight: 'bold' }}>
        עריכת פרטי ספק
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert ref={errorRef} severity="error">{error}</Alert>}

          {formStep === 'form' && (
            <>
              <TextField
                label="שם הספק *"
                value={formData.supplier_name || ''}
                onChange={handleChange('supplier_name')}
                fullWidth
                required
                disabled={isSubmitting}
              />

              <TextField
                label="ח.פ./ע.מ."
                value={formData.business_id || ''}
                onChange={handleChange('business_id')}
                fullWidth
                disabled={isSubmitting}
              />

              <TextField
                label="איש קשר"
                value={formData.contact_person || ''}
                onChange={handleChange('contact_person')}
                fullWidth
                disabled={isSubmitting}
              />

              <TextField
                label="טלפון"
                value={formData.phone || ''}
                onChange={handleChange('phone')}
                fullWidth
                disabled={isSubmitting}
              />

              <TextField
                label="אימייל"
                type="email"
                value={formData.email || ''}
                onChange={handleChange('email')}
                fullWidth
                disabled={isSubmitting}
              />

              <TextField
                label="כתובת"
                value={formData.address || ''}
                onChange={handleChange('address')}
                fullWidth
                disabled={isSubmitting}
              />

            </>
          )}

          {formStep === 'confirm' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                  <span>מעדכן...</span>
                </Box>
              ) : (
                <>
                  <Box sx={{ fontWeight: 'bold', fontSize: '1.6rem', mb: 1 }}>
                    אישור עדכון פרטים
                  </Box>
                  <Box><strong>שם הספק:</strong> {formData.supplier_name}</Box>
                  {formData.contact_person && (
                    <Box><strong>איש קשר:</strong> {formData.contact_person}</Box>
                  )}
                  {formData.phone && <Box><strong>טלפון:</strong> {formData.phone}</Box>}
                  {formData.email && <Box><strong>אימייל:</strong> {formData.email}</Box>}
                  {formData.address && <Box><strong>כתובת:</strong> {formData.address}</Box>}
                  {formData.business_id && <Box><strong>ח.פ./ע.מ.:</strong> {formData.business_id}</Box>}
                </>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} size="large" disabled={isSubmitting}>
          ביטול
        </Button>
        {formStep === 'form' && (
          <Button
            variant="contained"
            onClick={handleValidation}
            size="large"
            disabled={isSubmitting}
          >
            שלב הבא
          </Button>
        )}
        {formStep === 'confirm' && (
          <>
            <Button
              onClick={() => setFormStep('form')}
              size="large"
              disabled={isSubmitting}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              size="large"
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'אישור'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SupplierEditDialog;